import React, { useState } from "react";
import { Panel } from "../../../component/Panel";
import { Button } from "../../../component/Button";

interface Props {
  onUserPasswordChange: (password: string) => void;
  errorMessages: string[];
  disabled?: boolean;
  disabledReason?: string;
}

export const ChangeUserPasswordPanel: React.FunctionComponent<Props> = ({
  onUserPasswordChange,
  errorMessages,
  disabled,
  disabledReason,
}) => {
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<boolean>(false);
  const [
    passwordConfirmationError,
    setPasswordConfirmationError,
  ] = useState<boolean>(false);

  const verifyInput = () => {
    setErrorMessage(null);
    setPasswordError(false);
    setPasswordConfirmationError(false);

    if (password.trim().length === 0) {
      setErrorMessage("Empty password.");
      setPasswordError(true);
      return false;
    }

    if (password !== passwordConfirmation) {
      setErrorMessage("Passwords do not match.");
      setPasswordConfirmationError(true);
      return false;
    }

    return true;
  };

  return (
    <Panel>
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg leading-6 font-medium text-gray-900 mb-8">
          Change password
        </h3>
        <form>
          <div>
            <label
              htmlFor="username"
              className="block text-sm font-medium leading-5 text-gray-700"
            >
              New password
            </label>
            <div className="mt-1 rounded-md shadow-sm">
              <input
                id="username"
                type="password"
                disabled={disabled}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                className={`appearance-none block w-full px-3 py-2 border ${
                  passwordError && "border-red-500"
                } rounded-md placeholder-gray-400 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5`}
              />
            </div>
          </div>

          <div className="mt-6">
            <label
              htmlFor="password"
              className="block text-sm font-medium leading-5 text-gray-700"
            >
              Confirm new password
            </label>
            <div className="mt-1 rounded-md shadow-sm">
              <input
                disabled={disabled}
                id="password"
                type="password"
                value={passwordConfirmation}
                onChange={(event) =>
                  setPasswordConfirmation(event.target.value)
                }
                required
                className={`appearance-none block w-full px-3 py-2 border ${
                  passwordConfirmationError && "border-red-500"
                } rounded-md placeholder-gray-400 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5`}
              />
            </div>
          </div>

          {!!errorMessage && (
            <p className="text-xs italic text-red-500">{errorMessage}</p>
          )}

          {errorMessages.map((message, index) => (
            <p key={index} className="text-xs italic text-red-500">
              {message}
            </p>
          ))}

          {disabled && (
            <div className="flex justify-center items-center p-4">
              <p>{disabledReason}</p>
            </div>
          )}

          <div className="mt-6">
            <span className="block w-full rounded-md shadow-sm">
              <Button
                wide={true}
                disabled={disabled}
                onClick={(event) => {
                  event.preventDefault();
                  if (verifyInput()) {
                    onUserPasswordChange(password);
                    setPassword("");
                    setPasswordConfirmation("");
                  }
                }}
              >
                Change password
              </Button>
            </span>
          </div>
        </form>
      </div>
    </Panel>
  );
};
